import { ActionCreator, AjaxDispatcher } from './ajax-dispatcher';
import { getApiUrl, getCurrentLanguageId } from './common';
import HttpClient from './http-client';

interface DeliveryOption {
  id: string;
  element: HTMLElement;
  radio: HTMLInputElement;
  type: 'shipping' | 'payment';
  relatedDeliveryOptions: DeliveryOption[];
}

class DeliveryOptionComponentState {
  shippingGuid: string;
  paymentGuid: string;
}

export default class DeliveryOptionComponent {
  private shippings: DeliveryOption[];
  private payments: DeliveryOption[];

  private selectedShipping: DeliveryOption;
  private selectedPayment: DeliveryOption;

  constructor() {
    const deliveryOptions = this.createDeliveryOptions();
    this.shippings = deliveryOptions.filter(x => x.type === 'shipping');
    this.payments = deliveryOptions.filter(x => x.type === 'payment');

    this.initState();
    this.addEventListeners();
    this.updateUI();
  }

  private initState(): void {
    this.selectedShipping = this.shippings?.find(x => x.radio.checked);
    this.selectedPayment = this.payments?.find(x => x.radio.checked);
  }

  private async update(state: DeliveryOptionComponentState): Promise<void> {
    if (!this.stateChanged(state)) return;

    // update internal state
    this.updateState(state);

    // update UI
    this.updateUI();

    // send to API
    await this.persist();

    this.reloadSummaryComponents();
  }

  private stateChanged(newState: DeliveryOptionComponentState): boolean {
    const currentState = this.getCurrentState();
    return (newState.shippingGuid && newState.shippingGuid !== currentState.shippingGuid)
      || (newState.paymentGuid && newState.paymentGuid !== currentState.paymentGuid);
  }

  private updateState(state: DeliveryOptionComponentState): void {

    // get the new target state
    const currentState = this.getCurrentState();
    const newState = {
      shippingGuid: state.shippingGuid ?? currentState.shippingGuid,
      paymentGuid: state.paymentGuid ?? currentState.paymentGuid
    } as DeliveryOptionComponentState;

    // update shipping
    const shipping = this.shippings.find(x => x.id === newState.shippingGuid);
    this.selectedShipping = shipping;
    if (!this.selectedShipping) {
      const availableShippings = this.shippings;
      if (availableShippings.length === 1) {
        this.selectedShipping = availableShippings[0];
      }
    }

    // update payment
    const payment = this.payments.find(x => x.id === newState.paymentGuid);
    this.selectedPayment = payment && this.isAvailableForShipping(payment, this.selectedShipping) ? payment : null;
    if (!this.selectedPayment) {
      const availablePayments = this.payments.filter(x => this.isAvailableForShipping(x, this.selectedShipping));
      if (availablePayments.length === 1) {
        this.selectedPayment = availablePayments[0];
      }
    }
  }

  private updateUI(): void {
    this.updateShippingUI();
    this.updatePaymentUI();
  }

  private updateShippingUI(): void {
    this.shippings.forEach(shipping => {
      shipping.radio.checked = shipping.id === this.selectedShipping?.id;
    });
  }

  private updatePaymentUI(): void {
    this.payments.forEach(payment => {
      (!this.selectedShipping) || this.isAvailableForShipping(payment, this.selectedShipping)
        ? this.enable(payment)
        : this.disable(payment);
      payment.radio.checked = payment.id === this.selectedPayment?.id;
    });
  }

  private async persist(): Promise<void> {
    const url = `${getApiUrl()}/cart/delivery-options`;
    const httpClient = new HttpClient();
    await httpClient.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Current-Language-Id': getCurrentLanguageId()
      },
      body: JSON.stringify(this.getCurrentState())
    });
  }

  private getCurrentState(): DeliveryOptionComponentState {
    return {
      shippingGuid: this.selectedShipping?.id,
      paymentGuid: this.selectedPayment?.id
    } as DeliveryOptionComponentState;
  }

  private isAvailableForShipping(payment: DeliveryOption, shipping: DeliveryOption): boolean {
    return payment && shipping && shipping && shipping.relatedDeliveryOptions.some(x => x.id === payment.id);
  }

  private addEventListeners(): void {
    this.shippings.forEach(shipping => {
      shipping.element.addEventListener('click', (event: Event) => {
        // don't fire event for child elements
        if (shipping.element !== event.target) {
          event.preventDefault();
        } this.update({ shippingGuid: shipping.element.dataset.id } as DeliveryOptionComponentState)
      })
    });

    this.payments.forEach(payment => {
      payment.element.addEventListener('click', (event: Event) => {
        // don't fire event for child elements
        if (payment.element !== event.target) {
          event.preventDefault();
        }
        this.isEnabled(payment) && this.update({ paymentGuid: payment.element.dataset.id } as DeliveryOptionComponentState)
      })
    });
  }

  private reloadSummaryComponents(): void {
    const action = new ActionCreator().reloadComponents(['CartSummary', 'CartHeaderIcon']);
    new AjaxDispatcher().dispatchGetAction(action);
  }

  private createDeliveryOptions(): DeliveryOption[] {
    const deliveryOptions: DeliveryOption[] = [...document.querySelectorAll('[data-delivery-type]')]
      .map(x => x as HTMLElement)
      .map(element => {
        return {
          id: element.dataset.id,
          element: element,
          radio: element.querySelector('input') as HTMLInputElement,
          type: element.dataset.deliveryType as 'shipping' | 'payment',
          relatedDeliveryOptions: []
        };
      });

    deliveryOptions.forEach(
      option =>
      (option.relatedDeliveryOptions = this.getRelatedDeliveryOptions(
        option.element.dataset.related,
        deliveryOptions
      ))
    );

    return deliveryOptions;
  }

  private disable(option: DeliveryOption) {
    option.element.classList.add('disabled');
    option.radio.setAttribute('disabled', 'disabled');
    option.radio.checked = false;
    option.radio.disabled = true;
  }

  private enable(option: DeliveryOption) {
    option.element.classList.remove('disabled');
    option.radio.removeAttribute('disabled');
    option.radio.disabled = false;
  }

  private isEnabled(option: DeliveryOption) {
    return !option.radio.disabled;
  }

  private getRelatedDeliveryOptions(dataRelatedString: string, deliveryOptions: DeliveryOption[]): DeliveryOption[] {
    if (!dataRelatedString) {
      return [];
    }

    const result = dataRelatedString
      .split(';')
      .filter(x => x !== '')
      .map(id => deliveryOptions.find(d => d.id === id))
      .filter(x => x !== undefined);

    return result;
  }

}


